import { Flex, Select, Text } from "@chakra-ui/react";
import { useState } from "react";
import AppInput from "@/features/ui/app-input";
import AppButton from "@/features/line-sheet-sets/app-button";
import useIdentity from "@/features/ui/hooks/use-identity";
import useAppToasts from "@/features/line-sheet-sets/hooks/use-app-toasts";
import useUpdateLineSheetSetCommission from "@/features/line-sheet-sets/hooks/use-update-line-sheet-set-commission";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import { CommissionType } from "@/features/line-sheet-sets/line-sheet-set.types";

interface CommissionInlineEditProps {
  lineSheetSetId: number;
  value?: number | null;
  commissionType?: CommissionType | null;
  // 저장 성공 시 폼 값 동기화용
  onSaved: (value: number | null) => void;
  // 수수료 계산 단위 저장 성공 시 폼 값 동기화용
  onTypeSaved?: (commissionType: CommissionType) => void;
  // 저장 성공 시 서버 데이터 재검증(mutate) 트리거용
  onUpdated?: () => void;
}

/**
 * 수수료 전용 인라인 편집.
 * 발행 여부/전체 편집 모드와 무관하게 에이전시가 언제든지 수수료(%)와
 * 수수료 계산 단위(상품 단위 / 총 주문금액)를 수정할 수 있다.
 */
export default function CommissionInlineEdit({
  lineSheetSetId,
  value,
  commissionType,
  onSaved,
  onTypeSaved,
  onUpdated,
}: CommissionInlineEditProps) {
  const identity = useIdentity();
  const toasts = useAppToasts();
  const { t } = useI18nHelper();
  const { fire, isLoading } = useUpdateLineSheetSetCommission();
  const [isEditing, setIsEditing] = useState(false);
  const [input, setInput] = useState("");

  const currentType: CommissionType = commissionType ?? "ORDER";

  const canEdit =
    !!identity?.company &&
    identity.company.type === "AGENCY" &&
    !isLoading;

  const startEdit = () => {
    setInput(value != null ? String(value) : "");
    setIsEditing(true);
  };

  const save = () => {
    if (!canEdit) {
      return;
    }
    const commission = input !== "" ? parseFloat(input) : null;
    fire(
      { agencyId: identity!.company!.id, lineSheetSetId },
      { commission, commissionType: currentType },
      { rethrow: true }
    )
      .then(() => {
        toasts.info("수수료가 수정되었습니다.");
        onSaved(commission);
        setIsEditing(false);
        // 서버 데이터(라인시트세트) 재검증 — 수정한 수수료가 즉시 반영되도록
        onUpdated?.();
      })
      .catch((e) => {
        toasts.error(e);
      });
  };

  const changeType = (nextType: CommissionType) => {
    if (!canEdit || nextType === currentType) {
      return;
    }
    fire(
      { agencyId: identity!.company!.id, lineSheetSetId },
      { commission: value ?? null, commissionType: nextType },
      { rethrow: true }
    )
      .then(() => {
        toasts.info("수수료 계산 단위가 수정되었습니다.");
        onTypeSaved?.(nextType);
        onUpdated?.();
      })
      .catch((e) => {
        toasts.error(e);
      });
  };

  return (
    <Flex alignItems={"center"} gap={"8px"} flexWrap={"wrap"}>
      <Flex alignItems={"center"} gap={"6px"}>
        {!isEditing ? (
          <>
            <Text fontSize={"12px"}>{value != null ? `${value}%` : "-"}</Text>
            {canEdit && (
              <Text
                fontSize={"12px"}
                color={"#1272EF"}
                cursor={"pointer"}
                onClick={startEdit}
              >
                수정
              </Text>
            )}
          </>
        ) : (
          <>
            <AppInput
              type={"number"}
              width={"72px"}
              rightText={"%"}
              value={input}
              onChange={(v) => setInput(v ?? "")}
            />
            <AppButton
              size={"small"}
              variant={"primary"}
              isLoading={isLoading}
              onClick={save}
            >
              저장
            </AppButton>
            <AppButton
              size={"small"}
              variant={"gray"}
              onClick={() => setIsEditing(false)}
            >
              취소
            </AppButton>
          </>
        )}
      </Flex>
      {canEdit ? (
        <Select
          size={"xs"}
          width={"auto"}
          value={currentType}
          isDisabled={isLoading}
          onChange={(e) => changeType(e.target.value as CommissionType)}
        >
          <option value={"PRODUCT"}>{t("commission_type_product")}</option>
          <option value={"ORDER"}>{t("commission_type_order")}</option>
        </Select>
      ) : (
        <Text fontSize={"12px"} color={"#6F6F6C"}>
          {currentType === "ORDER"
            ? t("commission_type_order")
            : t("commission_type_product")}
        </Text>
      )}
    </Flex>
  );
}
